/**
 * The enrollment QR code.
 *
 * 🔴 ALWAYS dark-on-white, in both themes. A QR code is read by a camera, not by a person, and most scanners
 * refuse an inverted code — so this is the one element in the console that must NOT follow the viewer's
 * theme. It carries its own white ground and quiet zone rather than inheriting a surface colour.
 *
 * Encoding comes from `qrcode-generator` (zero dependencies of its own). Hand-rolling Reed-Solomon here
 * would put "does it scan?" on the far side of a bug nothing in CI could see.
 */
import qrcode from "qrcode-generator";

/**
 * Error correction: M. Enough to survive a smudged screen photo without inflating the module count.
 *
 * ⚠️ SIZE DIFFERS FROM THE DESIGN FILE, deliberately, and this is the reason. The design draws a decorative
 * 88px square inside a 120px box. A real otpauth URI encodes to 49 modules, so at 88px each module is ~1.5px
 * — below what a phone camera reliably resolves off a screen. Rendering at 108px inside the same 120px box
 * keeps the designed block while giving the modules room to be read. Told to the owner rather than done
 * quietly.
 */
export function QrCode({ value, size = 108 }: { value: string; size?: number }) {
  const qr = qrcode(0, "M");     // 0 = pick the smallest version that fits
  qr.addData(value);
  qr.make();

  const count = qr.getModuleCount();
  const quiet = 4;               // the QR spec's quiet zone — scanners find the code's edges by it
  const span = count + quiet * 2;

  // One path for every dark module beats thousands of <rect> nodes.
  let d = "";
  for (let r = 0; r < count; r++) {
    for (let c = 0; c < count; c++) {
      if (qr.isDark(r, c)) d += `M${c + quiet} ${r + quiet}h1v1h-1z`;
    }
  }

  return (
    <svg width={size} height={size} viewBox={`0 0 ${span} ${span}`} role="img"
      aria-label="QR code for setting up your authenticator app"
      shapeRendering="crispEdges" style={{ display: "block", borderRadius: 4 }}>
      <rect width={span} height={span} fill="#FFFFFF" />
      <path d={d} fill="#101321" />
    </svg>
  );
}

/** Base32 in groups of four — the difference between typing a secret correctly and giving up. */
export function groupSecret(secret: string): string {
  return (secret.match(/.{1,4}/g) ?? [secret]).join(" ");
}
